import { IS_EVIDENCE_LEADERBOARD_ENABLED } from '@/app-shell/flags/evidence-flags';
import { IS_SIDEBAR_REFERRAL_BUTTON_ENABLED } from '@/app-shell/flags/growth-flags';
import { NavItem } from '@/app-shell/layout/Navigation/sidebar';
import {
  useFooterRailCollapsible,
  type FooterMoreRowDescriptor,
} from '@/app-shell/layout/Navigation/sidebar/footer-rail/footer-rail-context';
import { useOfflineGatedTooltip } from '@/app-shell/offline-recording/use-offline-gated-tooltip';
import useReverseTrial from '@/hooks/useReverseTrial';
import { trackButtonClicked } from '@heidi/analytics/events';
import { ButtonAction, ScreenName } from '@heidi/analytics/vocabulary';
import { useIsFreeUser } from '@heidi/commerce/data';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@heidi/design-system/page-shell';
import {
  isAffiliateLinkModalOpenAtom,
  isEvidenceReferralModalOpenAtom,
} from '@heidi/growth';
import { isReferralProgramAvailableForLocale } from '@heidi/growth/data';
import { useTranslations } from '@heidi/runtime/i18n';
import { useSetAtom } from 'jotai';
import { Gift } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useEvidenceNavEnabled } from './hooks/useEvidenceNavEnabled';
import { useIsRibbonRail } from './hooks/useIsRibbonRail';

interface ReferralButtonProps {
  onOpen?: () => void;
}

export function ReferralButton({ onOpen }: ReferralButtonProps) {
  const intl = useIntl();
  // `useTranslations` stays for the resolved locale only; it is the app's locale
  // door and resolves a different ladder than `intl.locale`.
  const { locale } = useTranslations();
  const isRibbonRail = useIsRibbonRail();
  const { isOfflineGated, gatedTooltip } = useOfflineGatedTooltip();
  const setIsAffiliateLinkModalOpen = useSetAtom(isAffiliateLinkModalOpenAtom);
  const setIsEvidenceReferralModalOpen = useSetAtom(isEvidenceReferralModalOpenAtom);
  const { isFreeUser } = useIsFreeUser();
  const {
    isPracticeTrialUser,
    isClinicianTrialUser,
    isEvidenceTrialUser,
    isProTrialUser,
    isClinicianPracticeTrialUser,
    isInReverseTrial,
    isFakeClinicianTrial,
  } = useReverseTrial();
  const { showEvidenceFocusedNav } = useEvidenceNavEnabled();

  const isTrialUser =
    isPracticeTrialUser ||
    isClinicianTrialUser ||
    isEvidenceTrialUser ||
    isProTrialUser ||
    isClinicianPracticeTrialUser ||
    isInReverseTrial ||
    isFakeClinicianTrial;

  // Don't show if Evidence referral CTA is already visible for Evidence signup users
  const hasEvidenceReferralCta =
    showEvidenceFocusedNav && IS_EVIDENCE_LEADERBOARD_ENABLED();

  const shouldRender =
    IS_SIDEBAR_REFERRAL_BUTTON_ENABLED() &&
    isReferralProgramAvailableForLocale(locale) &&
    !isFreeUser &&
    !isTrialUser &&
    !hasEvidenceReferralCta;

  const handleClick = useCallback(() => {
    trackButtonClicked({
      screenName: ScreenName.Sidenav,
      action: ButtonAction.SidebarReferralButtonClicked,
      label: 'Refer and earn',
    });
    if (showEvidenceFocusedNav) {
      setIsEvidenceReferralModalOpen(true);
    } else {
      setIsAffiliateLinkModalOpen(true);
    }
    onOpen?.();
  }, [
    showEvidenceFocusedNav,
    setIsEvidenceReferralModalOpen,
    setIsAffiliateLinkModalOpen,
    onOpen,
  ]);

  const referLabel = intl.formatMessage({
    defaultMessage: 'Refer and earn',
    id: 'Yg/IaJxqA5',
    description: 'Sidebar CTA that opens the referral or affiliate-link modal',
  });
  const referDescriptor = useMemo<FooterMoreRowDescriptor>(
    () => ({
      id: 'refer',
      label: referLabel,
      icon: <Gift />,
      testId: 'sidebar-referral-button',
      onSelect: handleClick,
    }),
    [referLabel, handleClick]
  );
  const isReferCollapsed = useFooterRailCollapsible({
    id: 'refer',
    visible: Boolean(shouldRender) && isRibbonRail,
    pinned: false,
    descriptor: referDescriptor,
  });

  if (!shouldRender || isReferCollapsed) {
    return null;
  }

  // Ribbon rail: bare item — the shared footer <ul> is owned by SideNavFooter.
  if (isRibbonRail) {
    return (
      <SidebarMenuItem>
        <SidebarMenuButton
          layout="iconOnly"
          disabled={isOfflineGated}
          tooltip={gatedTooltip(referLabel)}
          onClick={isOfflineGated ? undefined : handleClick}
          aria-label={referLabel}
          data-testid="sidebar-referral-button"
        >
          <Gift
            className={isOfflineGated ? 'text-text-tertiary' : 'text-tier-pro-text'}
          />
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }

  return (
    <SidebarMenu>
      <NavItem
        label={referLabel}
        icon={
          <Gift
            className={
              isOfflineGated ? 'size-4 text-text-tertiary' : 'size-4 text-tier-pro-text'
            }
          />
        }
        onClick={handleClick}
        variant={isOfflineGated ? 'default' : 'emphasis'}
        testId="sidebar-referral-button"
      />
    </SidebarMenu>
  );
}
